import { useEffect } from "react";
import { FieldValues, UseFormReturn } from "react-hook-form";
import { mergeWithUserEdits, type DirtyFieldsValue } from "@/lib/utils";

/**
 * Syncs form with defaultValues when they change. When the form has unsaved edits,
 * merges new defaults with user changes (keeps dirty fields from current values).
 * When the form is pristine, resets to the new defaults.
 */
export function useFormMergeWithDefaults<T extends FieldValues>(
  form: UseFormReturn<T>,
  defaultValues: T,
) {
  useEffect(() => {
    if (!form.formState.isDirty) {
      form.reset(defaultValues);
      return;
    }
    const currentValues = form.getValues();
    const merged = mergeWithUserEdits(
      defaultValues,
      currentValues,
      form.formState.dirtyFields as DirtyFieldsValue,
    );
    form.reset(merged, { keepDirtyValues: true, keepDirty: true });
  }, [defaultValues, form]);
}
